/**
 * Cloudinary helper functions for generating optimized image URLs
 */

const CLOUDINARY_CLOUD_NAME = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;

interface CloudinaryOptions {
  width?: number;
  height?: number;
  quality?: number | 'auto';
  format?: 'auto' | 'webp' | 'jpg' | 'png';
  crop?: 'fill' | 'fit' | 'scale' | 'limit';
}

/**
 * Generate Cloudinary URL for an image
 * @param path - Path to the image (e.g., 'blog/tailler-barbe' or 'collections/2021-dark-grey/ALEXANDRA-GREY-DARK-GREY-01')
 * @param options - Cloudinary transformation options
 * @returns Cloudinary URL
 */
export function getCloudinaryUrl(
  path: string,
  options: CloudinaryOptions = {}
): string {
  if (!CLOUDINARY_CLOUD_NAME) {
    console.error('NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME is not defined');
    return '';
  }

  const {
    width,
    height,
    quality = 'auto',
    format = 'auto',
    crop = 'limit',
  } = options;

  // Remove /images/ prefix if present and file extension
  const cleanPath = path
    .replace(/^\/images\//, '')
    .replace(/\.(jpg|jpeg|png|webp)$/i, '');

  // Build transformation string
  const transformations: string[] = [];

  if (width || height) {
    const dimensions = [
      width ? `w_${width}` : '',
      height ? `h_${height}` : '',
      `c_${crop}`,
    ]
      .filter(Boolean)
      .join(',');
    transformations.push(dimensions);
  }

  transformations.push(`q_${quality}`);
  transformations.push(`f_${format}`);

  const transformString = transformations.join('/');

  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${transformString}/${cleanPath}`;
}

/**
 * Generate Cloudinary URL for a blog image
 * @param fileName - File name without extension (e.g., 'tailler-barbe')
 * @param options - Cloudinary transformation options
 * @returns Cloudinary URL
 */
export function getBlogImageUrl(
  fileName: string,
  options: CloudinaryOptions = {}
): string {
  const cleanFileName = fileName.replace(/\.(webp|jpg|jpeg|png)$/i, '');
  return getCloudinaryUrl(cleanFileName, options);
}

/**
 * Generate Cloudinary URL for a collection image
 * @param collectionFolder - Collection folder name (e.g., '2021-dark-grey')
 * @param fileName - File name without extension
 * @param options - Cloudinary transformation options
 * @returns Cloudinary URL
 */
export function getCollectionImageUrl(
  collectionFolder: string,
  fileName: string,
  options: CloudinaryOptions = {}
): string {
  const cleanFileName = fileName.replace(/\.(jpg|jpeg|png|webp)$/i, '');
  return getCloudinaryUrl(cleanFileName, options);
}

/**
 * Generate Cloudinary URL for a video
 * @param fileName - File name without extension (e.g., 'head-spa-hero')
 * @param options - Cloudinary transformation options
 * @returns Cloudinary video URL
 */
export function getVideoUrl(
  fileName: string,
  options: CloudinaryOptions = {}
): string {
  if (!CLOUDINARY_CLOUD_NAME) {
    console.error('NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME is not defined');
    return '';
  }

  const {
    width,
    height,
    quality = 'auto',
    format = 'auto',
  } = options;

  // Remove file extension if present
  const cleanFileName = fileName.replace(/\.(mp4|webm|mov)$/i, '');

  // Build transformation string
  const transformations: string[] = [];

  if (width || height) {
    const dimensions = [
      width ? `w_${width}` : '',
      height ? `h_${height}` : '',
    ]
      .filter(Boolean)
      .join(',');
    transformations.push(dimensions);
  }

  transformations.push(`q_${quality}`);
  if (format !== 'auto') {
    transformations.push(`f_${format}`);
  }

  const transformString = transformations.join('/');

  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload/${transformString}/${cleanFileName}`;
}
